import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AirportDropDownList,
  LocationComponent,
  UiStateManagementService
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  DatePicker,
  DateRange,
  Dropdown
} from '@com-delta-omnichannel/mach-core-js';
import { Subject } from 'rxjs';
import {
  StaysFormErrors,
  StaysWidget,
  StaysWidgetForm
} from '../models/stays-widget.interfaces';
import { ComponentLifecycleService } from '../services/component-lifecycle.service';
import { ErrorHandlingService } from '../services/error-handling.service';
import { InitializationService } from '../services/initialization.service';
import { StaysWidgetHelperBaseComponent } from './stays-widget-helper-base.component';
@Component({
  selector: 'idp-stays-widget-base',
  standalone: true,
  imports: [CommonModule],
  template: '',
  styles: []
})
export class StaysWidgetBaseComponent extends StaysWidgetHelperBaseComponent {
  uiStateService = inject(UiStateManagementService);
  lifecycleService = inject(ComponentLifecycleService);
  initializationService = inject(InitializationService);
  errorHandlingService = inject(ErrorHandlingService);
  elementRef = inject(ElementRef);
  destroy$ = new Subject<void>();
  eventService = this.lifecycleService.eventService;
  @Input() content: StaysWidget = {} as StaysWidget;
  @Input() viewSearchOptionExpanded = false;
  @Input() locationSelected: AirportDropDownList = { code: '', name: '' };
  @Input() staysWidgetForm!: FormGroup<StaysWidgetForm>;
  @Input() closeModal = true;
  @Input() resetErrors = false;
  @Output() expandEmit = new EventEmitter<boolean>(false);
  @Output() openModalEmit = new EventEmitter<boolean>();
  @Output() formSubmitted = new EventEmitter<string>();
  @Output() errorStateChange = new EventEmitter<{
    message: string;
    count: number;
    firstErrorElement: HTMLElement | null;
  }>();

  @Output() analyticsEvent = new EventEmitter<HTMLElement>();
  @ViewChild(LocationComponent) locationComponent!: LocationComponent;
  datePickerInstance!: DatePicker;
  dropDownTravelerOptions!: Dropdown;
  dropDownRoomOptions!: Dropdown;
  expandView = false;
  isDatePickerOpen = false;

  getError(errors: StaysFormErrors) {
    const result = this.errorHandlingService.processErrors(
      errors,
      this.staysWidgetForm,
      this.elementRef.nativeElement
    );
    this.destinationErrorMessage = errors.destinationErrorLabel;
    this.datesErrorMessage = errors.dateError;
    this.tooFewPeopleErrorMessage = errors.travelerError;
    this.tooFewRoomsErrorMessage = errors.roomsError;
    this.maxDatesErrorMessage = errors.maxDatesError;
    this.errorCount = result.errorCount;
    this.firstErrorElement = result.firstErrorElement;
  }

  handleErrorCountChange(): void {
    if (this.isSubmitted) {
      const errors = this.lifecycleService.processFormSubmission(
        this.staysWidgetForm,
        this.content
      );
      this.getError(errors);
    }
  }

  updateFormDates(dates: DateRange): void {
    if (this.staysWidgetForm && dates) {
      this.staysWidgetForm.patchValue({
        checkInDate: dates.start,
        checkOutDate: dates.end
      });
    }
  }

  openDatePicker(): void {
    this.isDatePickerOpen = !this.isDatePickerOpen;
    if (this.datePickerInstance.isOpen()) {
      this.datePickerInstance.close();
    } else {
      this.datePickerInstance.open();
    }
  }

  focusFirstInvalidFieldHandler(formGroup: FormGroup<StaysWidgetForm>): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.controls[key as keyof StaysWidgetForm];
      if (control.enabled && control.status === 'INVALID') {
        this.focusElement(key);
        break;
      }
    }
  }

  focusElement(key: string): void {
    const focusMap: Record<string, () => void> = {
      location: () => this.focusLocationElement(),
      checkInDate: () => this.focusItem('#range-date-trigger'),
      checkOutDate: () => this.focusItem('#range-date-trigger'),
      numberOfRooms: () => this.focusItem('#dropdownRoom'),
      numberOfTravelers: () => this.focusItem('#dropdownTraveler')
    };
    focusMap[key]?.();
  }

  private focusLocationElement(): void {
    requestAnimationFrame(() => {
      const location =
        this.elementRef.nativeElement.querySelector('idp-location');
      if (location) {
        (location as HTMLElement).focus();
      }
    });
  }

  private focusItem(item: string): void {
    const element = this.elementRef.nativeElement.querySelector(item);
    if (element) {
      (element as HTMLElement).focus();
    }
  }
}
